import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiCalendar,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiCheckSquare,
  FiClock,
  FiAlertCircle,
  FiList,
  FiSearch,
  FiBarChart2,
  FiPackage,
  FiTrendingUp,
  FiInfo,
} from "react-icons/fi";

import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { withAuth } from "../../../components/HOC/withAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import CardInfo from "../../../components/CustomUI/CardInfo";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";

import styles from "./ProductionPlanning.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const STATUS_OPTIONS = [
  { value: "planned", label: "Planlandı" },
  { value: "in_progress", label: "Devam Ediyor" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal Edildi" },
];

const STATUS_COLORS = {
  planned: "#3b82f6",
  in_progress: "#f59e0b",
  completed: "#10b981",
  cancelled: "#ef4444",
};

const STATUS_LABELS = {
  planned: "Planlandı",
  in_progress: "Devam Ediyor",
  completed: "Tamamlandı",
  cancelled: "İptal Edildi",
};

const EMPTY_FORM = {
  title: "",
  partId: "",
  productionLineId: "",
  plannedQuantity: "",
  startDate: "",
  endDate: "",
  operatorNote: "",
  status: "planned",
};

const GROUPED_PLANS_COLUMNS = [
  { name: "", dataType: "text", id: 0, sortable: false },
  { name: "Sipariş Adı", dataType: "text", id: 1, sortable: false },
  { name: "Model Sayısı", dataType: "number", id: 2, sortable: false },
  { name: "Toplam Planlanan", dataType: "number", id: 3, sortable: false },
];

const FEASIBILITY_COLUMNS = [
  { name: "Parça", dataType: "text", id: 0 },
  { name: "Kaynak", dataType: "text", id: 1 },
  { name: "Maks. Üretilebilir", dataType: "number", id: 3 },
  { name: "Eksik Bileşen", dataType: "number", id: 4 },
];

const BOM_SOURCE_LABELS = {
  finalProduct: "Son Ürün",
  partMachine: "Üretim",
  partAssembly: "Montaj",
};

const SHORTAGE_COLUMNS = [
  { name: "Bileşen", dataType: "text", id: 0 },
  { name: "Kod", dataType: "text", id: 1 },
  { name: "Tüketim / Adet", dataType: "number", id: 2 },
  { name: "Toplam İhtiyaç", dataType: "number", id: 3 },
  { name: "Mevcut Stok", dataType: "number", id: 4 },
  { name: "Eksik Miktar", dataType: "number", id: 5 },
  { name: "Durum", dataType: "text", id: 6, sortable: false },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => (
  <span
    className={styles.statusBadge}
    style={{
      background: STATUS_COLORS[status] + "22",
      color: STATUS_COLORS[status],
    }}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

// ─── Progress Bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, max, color = "#10b981" }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className={styles.progressPct}>{pct}%</span>
    </div>
  );
};

// ─── BOM Detail Table (reusable inline) ──────────────────────────────────────
const BomDetailTable = ({ bomLines, styles: s, indent = 0 }) => (
  <div
    className={s.bomDetail}
    style={indent ? { marginLeft: indent } : undefined}
  >
    <div className={s.bomDetailTitle}>
      <FiList size={14} /> Malzeme Listesi (BOM) — 1 adet üretim için
    </div>
    <table className={s.bomTable}>
      <thead>
        <tr>
          <th>Bileşen</th>
          <th>Tüketim / Adet</th>
          <th>Mevcut Stok</th>
          <th>Karşılanan Üretim</th>
          <th>Durum</th>
        </tr>
      </thead>
      <tbody>
        {bomLines.map((bline, i) => (
          <tr key={i}>
            <td>
              <div style={{ fontWeight: 600 }}>{bline.partName}</div>
              <div
                style={{ fontSize: 11, color: "var(--m-text-subtle, #9ca3af)" }}
              >
                {bline.partCode}
              </div>
            </td>
            <td>{bline.consumptionRatio} / adet</td>
            <td>
              <strong
                style={{
                  color: bline.availableQty > 0 ? undefined : "#ef4444",
                }}
              >
                {bline.availableQty}
              </strong>
            </td>
            <td>
              <strong
                style={{
                  color: bline.coverageUnits > 0 ? "#10b981" : "#ef4444",
                }}
              >
                {bline.coverageUnits} adet
              </strong>
            </td>
            <td>
              {bline.shortageQty > 0 ? (
                <span
                  style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                >
                  {bline.shortageQty} eksik
                </span>
              ) : (
                <span style={{ color: "#10b981", fontSize: 12 }}>
                  ✓ Yeterli
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Plan Shortage Tree (Nested) ────────────────────────────────────────────────
const PlanShortageTree = ({ plan, authHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPaths, setExpandedPaths] = useState(new Set());

  useEffect(() => {
    const fetchShortage = async () => {
      setLoading(true);
      try {
        const payload = { quantity: plan.plannedQuantity, ignoreStock: true };
        if (plan.deviceModelId) {
          payload.deviceModelId = plan.deviceModelId._id || plan.deviceModelId;
        } else if (plan.partId) {
          payload.partId = plan.partId._id || plan.partId;
        } else {
          setError("Model veya parça bilgisi bulunamadı.");
          setLoading(false);
          return;
        }

        const res = await axios.post(
          `${API_URL}/api/production-planning/shortage`,
          payload,
          { headers: authHeaders() },
        );
        setData(res.data.data);
      } catch (err) {
        setError(err?.response?.data?.message || "Hesaplama hatası");
      } finally {
        setLoading(false);
      }
    };
    fetchShortage();
  }, [plan, authHeaders]);

  const getVisibleRows = useCallback(() => {
    if (!data?.bomTree) return [];
    const visible = [];

    function traverse(node, depth = 0, parentPath = "") {
      const currentPath = parentPath
        ? `${parentPath}/${node.partId}`
        : node.partId;
      const row = {
        ...node,
        depth,
        path: currentPath,
        hasChildren: node.children && node.children.length > 0,
      };

      if (node.partId !== "model-root") {
        visible.push(row);
      }

      const isModelRoot = node.partId === "model-root";
      const childDepth = isModelRoot ? 0 : depth + 1;
      const shouldTraverseChildren =
        isModelRoot ||
        (node.children &&
          node.children.length > 0 &&
          expandedPaths.has(currentPath));

      if (shouldTraverseChildren && node.children) {
        node.children.forEach((child) =>
          traverse(child, childDepth, currentPath),
        );
      }
    }

    traverse(data.bomTree, 0, "");
    return visible;
  }, [data, expandedPaths]);

  if (loading)
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <FiRefreshCw className="spin" style={{ marginRight: 8 }} />{" "}
        Yükleniyor...
      </div>
    );
  if (error)
    return (
      <div style={{ padding: 20, textAlign: "center", color: "#ef4444" }}>
        {error}
      </div>
    );
  if (!data?.bomTree)
    return (
      <div style={{ padding: 20, textAlign: "center", color: "#6b7280" }}>
        BOM detayı bulunamadı.
      </div>
    );

  return (
    <div
      style={{
        padding: 16,
        background: "#fff",
        borderTop: "1px solid #e5e7eb",
      }}
    >
      <div style={{ marginBottom: 12, fontWeight: 600 }}>
        {data.deviceModel
          ? `Model: ${data.deviceModel.code}`
          : `Parça: ${data.partName}`}{" "}
        - Eksik Malzeme Analizi
      </div>
      <Table columns={SHORTAGE_COLUMNS} search={false} hideFooter>
        {getVisibleRows().map((bline, i) => {
          // Eğer bu satır bizzat üretilecek olan nihai ürünse (Son Ürün) stok karşılaştırması yapılmamalı
          const isSonUrun =
            bline.depth === 0 &&
            (!data.deviceModel || bline.sourceType === "finalProduct");

          const pct = isSonUrun
            ? 0
            : bline.neededQty > 0
              ? Math.min(
                  100,
                  Math.round((bline.availableQty / bline.neededQty) * 100),
                )
              : 100;
          const barColor =
            pct === 100 ? "#10b981" : pct > 50 ? "#f59e0b" : "#ef4444";
          const isExpanded = expandedPaths.has(bline.path);
          return (
            <Tr
              key={i}
              style={{
                cursor: bline.hasChildren ? "pointer" : "default",
                background:
                  bline.shortageQty > 0 && !isSonUrun
                    ? "#fff8f8"
                    : isSonUrun
                      ? "#f0fdf4"
                      : undefined,
              }}
              onClick={(e) => {
                if (bline.hasChildren) {
                  e.stopPropagation();
                  const newPaths = new Set(expandedPaths);
                  if (isExpanded) {
                    newPaths.delete(bline.path);
                  } else {
                    newPaths.add(bline.path);
                  }
                  setExpandedPaths(newPaths);
                }
              }}
            >
              <Td>
                <div
                  className="d-flex align-items-center gap-2 flex-wrap"
                  style={{ paddingLeft: bline.depth * 20 }}
                >
                  {bline.hasChildren && (
                    <span
                      style={{
                        color: "var(--m-text-subtle, #9ca3af)",
                        display: "inline-flex",
                        alignItems: "center",
                        marginRight: 4,
                      }}
                    >
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </span>
                  )}
                  {!bline.hasChildren && bline.depth > 0 && (
                    <span style={{ width: 20, display: "inline-block" }} />
                  )}
                  <strong>{bline.partName}</strong>
                  {bline.sourceType && (
                    <span
                      className={styles.chip}
                      style={{
                        background:
                          bline.sourceType === "finalProduct"
                            ? "#ede9fe"
                            : bline.sourceType === "partAssembly"
                              ? "#fef3c7"
                              : "#dbeafe",
                        color:
                          bline.sourceType === "finalProduct"
                            ? "#7c3aed"
                            : bline.sourceType === "partAssembly"
                              ? "#92400e"
                              : "#1d4ed8",
                      }}
                    >
                      {BOM_SOURCE_LABELS[bline.sourceType] || bline.sourceType}
                    </span>
                  )}
                </div>
              </Td>
              <Td
                style={{ color: "var(--m-text-subtle, #9ca3af)", fontSize: 12 }}
              >
                {bline.partCode}
              </Td>
              <Td>{bline.consumptionRatio}</Td>
              <Td>
                <strong>{bline.neededQty}</strong>
              </Td>
              <Td>
                {isSonUrun ? (
                  <span style={{ color: "#6b7280" }}>—</span>
                ) : (
                  <span
                    style={{
                      fontWeight: 600,
                      color:
                        bline.availableQty >= bline.neededQty
                          ? "#10b981"
                          : "#ef4444",
                    }}
                  >
                    {bline.availableQty}
                  </span>
                )}
              </Td>
              <Td>
                {isSonUrun ? (
                  <span style={{ color: "#6b7280" }}>—</span>
                ) : bline.shortageQty > 0 ? (
                  <span style={{ color: "#ef4444", fontWeight: 700 }}>
                    {bline.shortageQty}
                  </span>
                ) : (
                  <span style={{ color: "#10b981" }}>—</span>
                )}
              </Td>
              <Td>
                {isSonUrun ? (
                  <span
                    style={{ color: "#3b82f6", fontSize: 12, fontWeight: 600 }}
                  >
                    Üretilecek
                  </span>
                ) : bline.shortageQty > 0 ? (
                  <span
                    style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                  >
                    <FiAlertCircle size={12} style={{ marginRight: 4 }} /> Eksik
                  </span>
                ) : (
                  <span style={{ color: "#10b981", fontSize: 12 }}>
                    <FiCheckSquare size={12} style={{ marginRight: 4 }} />{" "}
                    Yeterli
                  </span>
                )}
              </Td>
            </Tr>
          );
        })}
      </Table>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Plans = () => {
  const { getToken } = useAuth();
  useTitle("Üretim Planları");

  const [toasts, setToasts] = useState([]);

  // Plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPartId, setFilterPartId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [expandedSales, setExpandedSales] = useState({});
  const [expandedPlans, setExpandedPlans] = useState({});

  // Feasibility
  const [feasibility, setFeasibility] = useState([]);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);
  const [feasibilityFilter, setFeasibilityFilter] = useState("");
  const [expandedFeasRow, setExpandedFeasRow] = useState(null);
  const [expandedVariantRow, setExpandedVariantRow] = useState(null);

  // Shortage
  const [shortageMode, setShortageMode] = useState("part"); // "part" | "product"
  const [shortageMainPartId, setShortageMainPartId] = useState("");
  const [shortageVariantId, setShortageVariantId] = useState("");
  const [shortageModelId, setShortageModelId] = useState("");
  const [shortageVariants, setShortageVariants] = useState([]);
  const [shortageVariantsLoading, setShortageVariantsLoading] = useState(false);
  const [shortageQty, setShortageQty] = useState("");
  const [shortageResult, setShortageResult] = useState(null);
  const [shortageLoading, setShortageLoading] = useState(false);

  // Shared
  const [parts, setParts] = useState([]);
  const [lines, setLines] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4000,
    );
  }, []);

  const fetchParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts`, {
        headers: authHeaders(),
      });
      const list = res.data?.parts ?? res.data?.data ?? [];
      setParts(Array.isArray(list) ? list : []);
    } catch {
      setParts([]);
    }
  }, [authHeaders]);

  const fetchLines = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/production-lines`, {
        headers: authHeaders(),
      });
      const list = res.data?.lines ?? res.data?.data ?? [];
      setLines(Array.isArray(list) ? list : []);
    } catch {
      setLines([]);
    }
  }, [authHeaders]);

  const fetchDeviceModels = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/device-models`, {
        headers: authHeaders(),
      });
      const list = res.data?.models ?? res.data?.deviceModels ?? res.data ?? [];
      setDeviceModels(Array.isArray(list) ? list : []);
    } catch {
      setDeviceModels([]);
    }
  }, [authHeaders]);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPartId) params.partId = filterPartId;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;
      const res = await axios.get(`${API_URL}/api/production-planning/plans`, {
        headers: authHeaders(),
        params,
      });
      setPlans(res.data?.data || []);
    } catch {
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [authHeaders, filterStatus, filterPartId, filterStartDate, filterEndDate]);

  const fetchFeasibility = useCallback(async () => {
    setFeasibilityLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/production-planning/feasibility`,
        {
          headers: authHeaders(),
        },
      );
      setFeasibility(res.data?.data || []);
    } catch {
      setFeasibility([]);
    } finally {
      setFeasibilityLoading(false);
    }
  }, [authHeaders]);

  const fetchPartVariants = useCallback(
    async (mainId) => {
      if (!mainId) {
        setShortageVariants([]);
        setShortageVariantId("");
        return;
      }
      setShortageVariantsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/parts/${mainId}`, {
          headers: authHeaders(),
        });
        const vars = res.data?.variants || [];
        setShortageVariants(vars);
        setShortageVariantId("");
      } catch {
        setShortageVariants([]);
        setShortageVariantId("");
      } finally {
        setShortageVariantsLoading(false);
      }
    },
    [authHeaders],
  );

  const fetchPlansRef = useRef(fetchPlans);
  const fetchFeasibilityRef = useRef(fetchFeasibility);
  fetchPlansRef.current = fetchPlans;
  fetchFeasibilityRef.current = fetchFeasibility;

  const handleTabChange = useCallback((tabIndex) => {
    if (tabIndex === 0) fetchPlansRef.current();
    else if (tabIndex === 1) fetchFeasibilityRef.current();
  }, []);

  useEffect(() => {
    fetchParts();
    fetchLines();
    fetchDeviceModels();
    fetchPlans();
  }, [fetchParts, fetchLines, fetchDeviceModels, fetchPlans]);

  // Grouping logic
  const getSaleName = (note) => {
    if (!note) return "Bilinmeyen Sipariş";
    const lines = note.split("\n");
    const saleLine = lines.find((l) => l.startsWith("Sipariş:"));
    if (saleLine) return saleLine.replace("Sipariş:", "").trim();
    return "Bilinmeyen Sipariş";
  };

  const getModelName = (note) => {
    if (!note) return "";
    const lines = note.split("\n");
    const modelLine = lines.find((l) => l.startsWith("Model:"));
    if (modelLine) return modelLine.replace("Model:", "").trim();
    return "";
  };

  const groupedPlans = React.useMemo(() => {
    const groups = {};
    plans.forEach((plan) => {
      const saleName = getSaleName(plan.operatorNote);
      if (!groups[saleName]) {
        groups[saleName] = {
          saleName,
          plans: [],
          totalPlanned: 0,
          totalProduced: 0,
          totalStock: 0,
        };
      }
      groups[saleName].plans.push(plan);
      groups[saleName].totalPlanned += plan.plannedQuantity || 0;
      groups[saleName].totalProduced += plan.producedQuantity || 0;
      groups[saleName].totalStock += plan.currentStock || 0;
    });
    // Sort by latest plan's startDate
    return Object.values(groups).sort((a, b) => {
      const aDate = a.plans[0]?.startDate || "";
      const bDate = b.plans[0]?.startDate || "";
      return bDate.localeCompare(aDate);
    });
  }, [plans]);

  const toggleSaleRow = (saleName) => {
    setExpandedSales((prev) => ({
      ...prev,
      [saleName]: !prev[saleName],
    }));
  };

  const togglePlanRow = (planId) => {
    setExpandedPlans((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));
  };

  const handleStatusChange = async (plan, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/production-planning/plans/${plan._id}/status`,
        { status: newStatus },
        { headers: authHeaders() },
      );
      addToast("Durum güncellendi.");
      fetchPlans();
    } catch {
      addToast("Durum güncellenemedi.", "error");
    }
  };

  const resetShortageForm = () => {
    setShortageMainPartId("");
    setShortageVariantId("");
    setShortageModelId("");
    setShortageVariants([]);
    setShortageResult(null);
  };

  const runShortage = async () => {
    const isProductMode = shortageMode === "product";
    const effectivePartId = shortageVariantId || shortageMainPartId;

    if (isProductMode ? !shortageModelId : !effectivePartId) {
      addToast(
        isProductMode ? "Model ve miktar seçin." : "Parça ve miktar seçin.",
        "error",
      );
      return;
    }
    if (!shortageQty) {
      addToast("Miktar girin.", "error");
      return;
    }

    setShortageLoading(true);
    setShortageResult(null);
    try {
      const payload = isProductMode
        ? { deviceModelId: shortageModelId, quantity: Number(shortageQty) }
        : { partId: effectivePartId, quantity: Number(shortageQty) };
      const res = await axios.post(
        `${API_URL}/api/production-planning/shortage`,
        payload,
        { headers: authHeaders() },
      );
      setShortageResult(res.data?.data || null);
    } catch (err) {
      addToast(err?.response?.data?.message || "Hesaplama hatası.", "error");
    } finally {
      setShortageLoading(false);
    }
  };

  const planStats = {
    total: plans.length,
    planned: plans.filter((p) => p.status === "planned").length,
    inProgress: plans.filter((p) => p.status === "in_progress").length,
    completed: plans.filter((p) => p.status === "completed").length,
  };

  const allParts = Array.isArray(parts) ? parts : [];
  const partOptions = allParts.map((p) => ({
    value: p._id,
    label: `${p.name} (${p.code})`,
  }));
  const shortagePartOptions = allParts
    .filter((p) => !p.isFinalProduct)
    .map((p) => ({
      value: p._id,
      label: `${p.name} (${p.code})`,
    }));
  const modelOptions = (Array.isArray(deviceModels) ? deviceModels : []).map(
    (m) => ({
      value: m._id,
      label: `${m.code} — ${m.name}`,
    }),
  );
  const lineOptions = (Array.isArray(lines) ? lines : []).map((l) => ({
    value: l._id,
    label: l.name || l.code,
  }));

  // Group feasibility: main parts as rows, variants as sub-rows.
  // Search matches any main part or any of its variants.
  const q = feasibilityFilter.toLowerCase();
  const feasMainItems = feasibility.filter((f) => !f.mainPartId);
  const feasVariantMap = feasibility.reduce((acc, f) => {
    if (f.mainPartId) {
      if (!acc[f.mainPartId]) acc[f.mainPartId] = [];
      acc[f.mainPartId].push(f);
    }
    return acc;
  }, {});

  const groupedFeasibility = feasMainItems
    .map((main) => ({
      ...main,
      variants: feasVariantMap[main.partId] || [],
    }))
    .filter((g) => {
      if (!q) return true;
      const matchesMain =
        g.partName?.toLowerCase().includes(q) ||
        g.partCode?.toLowerCase().includes(q);
      const matchesVariant = g.variants.some(
        (v) =>
          v.partName?.toLowerCase().includes(q) ||
          v.partCode?.toLowerCase().includes(q),
      );
      return matchesMain || matchesVariant;
    });

  // Also include parts that are variants but have no matching main (orphan variants shown standalone)
  const orphanVariants = feasibility.filter(
    (f) =>
      f.mainPartId &&
      !feasMainItems.find((m) => m.partId === f.mainPartId) &&
      (!q ||
        f.partName?.toLowerCase().includes(q) ||
        f.partCode?.toLowerCase().includes(q)),
  );

  const totalFeasCount =
    groupedFeasibility.length +
    groupedFeasibility.reduce((s, g) => s + g.variants.length, 0) +
    orphanVariants.length;

  // Shortage tab: variant options for selected main part
  const shortageVariantOptions = shortageVariants.map((v) => ({
    value: v._id,
    label: `${v.name} (${v.code})`,
  }));

  return (
    <AppPage
      breadcrumbs={[{ name: "Üretim Yönetimi" }, { name: "Üretim Planları" }]}
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} type={t.type}>
            {t.message}
          </Toast>
        ))}
      </ToastContainer>

      <div className="mlm-header card card-body p-3 mb-3">
        <div className="mlm-header-left">
          <h2 className="mlm-title">Üretim Planları</h2>
        </div>
      </div>
      <Panel>
        {/* <CardContainer>
              <CardInfo title="Toplam Plan" value={planStats.total} icon={<FiList size={20} />} color="#6366f1" />
              <CardInfo title="Planlandı" value={planStats.planned} icon={<FiCalendar size={20} />} color={STATUS_COLORS.planned} />
              <CardInfo title="Devam Ediyor" value={planStats.inProgress} icon={<FiClock size={20} />} color={STATUS_COLORS.in_progress} />
              <CardInfo title="Tamamlandı" value={planStats.completed} icon={<FiCheckSquare size={20} />} color={STATUS_COLORS.completed} />
            </CardContainer> */}
        <Container>
          <ContainerItem>
            <Table
              columns={GROUPED_PLANS_COLUMNS}
              loading={plansLoading}
              search={false}
              hideFooter={true}
            >
              {groupedPlans.flatMap((group) => {
                const isExpanded = expandedSales[group.saleName];
                const pct =
                  group.totalPlanned > 0
                    ? Math.min(
                        100,
                        Math.round(
                          (group.totalProduced / group.totalPlanned) * 100,
                        ),
                      )
                    : 0;
                const progressColor =
                  pct === 100 ? "#10b981" : pct > 50 ? "#f59e0b" : "#3b82f6";

                return [
                  <Tr
                    key={group.saleName}
                    onClick={() => toggleSaleRow(group.saleName)}
                    style={{ cursor: "pointer" }}
                    className={isExpanded ? "table-active" : ""}
                  >
                    <Td className="text-center">
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </Td>
                    <Td>
                      <strong>{group.saleName}</strong>
                    </Td>
                    <Td>{group.plans.length} Model</Td>
                    <Td>{group.totalPlanned}</Td>
                  </Tr>,
                  isExpanded && (
                    <Tr key={group.saleName + "_expanded"}>
                      <Td
                        colSpan={5}
                        style={{
                          padding: 0,
                          background: "var(--m-background, #f9fafb)",
                        }}
                      >
                        <div className={styles.bomDetail}>
                          <h6
                            className="mb-2 text-primary d-flex align-items-center gap-2"
                            style={{ fontSize: 13, fontWeight: 600 }}
                          >
                            <FiList /> {group.saleName} Plan Detayları
                          </h6>
                          <table className={styles.bomTable}>
                            <thead>
                              <tr>
                                <th>Model / Plan</th>
                                <th>Planlanan</th>
                                <th>Tarih</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.plans.map((plan) => {
                                const isSufficient =
                                  (plan.currentStock ?? 0) >=
                                  (plan.plannedQuantity ?? 0);
                                const isPlanExpanded = expandedPlans[plan._id];
                                return (
                                  <React.Fragment key={plan._id}>
                                    <tr
                                      style={{
                                        cursor: "pointer",
                                        background: isPlanExpanded
                                          ? "var(--m-background, #f3f4f6)"
                                          : "inherit",
                                      }}
                                      onClick={() => togglePlanRow(plan._id)}
                                    >
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          {isPlanExpanded ? (
                                            <FiChevronUp />
                                          ) : (
                                            <FiChevronDown />
                                          )}
                                          <strong>
                                            {getModelName(plan.operatorNote) ||
                                              plan.title}
                                          </strong>
                                        </div>
                                      </td>
                                      <td>{plan.plannedQuantity}</td>
                                      <td>
                                        {plan.saleDate
                                          ? plan.saleDate.slice(0, 10)
                                          : plan.startDate?.slice(0, 10)}
                                      </td>
                                    </tr>
                                    {isPlanExpanded && (
                                      <tr>
                                        <td colSpan={3} style={{ padding: 0 }}>
                                          <PlanShortageTree
                                            plan={plan}
                                            authHeaders={authHeaders}
                                          />
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </Td>
                    </Tr>
                  ),
                ].filter(Boolean);
              })}
            </Table>
          </ContainerItem>
        </Container>{" "}
      </Panel>
    </AppPage>
  );
};

export default withAuth(Plans, ["admin", "employee"]);
